import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { ProgressBackdrop } from 'ui/src/components';
import { GridArea } from '../../components/common/GridArea';
import { useRecoilValue } from 'recoil';
import globalState from 'ui/src/state/globalState';
import AdminApi from '../../api/AdminApi';
import { GridOptions } from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import 'react-datepicker/dist/react-datepicker.css';
import ReactDatePicker from 'react-datepicker';

const ServiceListGridDefs: GridOptions = {
  columnDefs: [
    {
      headerName: '날짜',
      field: 'access_date',
      flex: 1,
      minWidth: 120,
      cellStyle: { textAlign: 'center' },
    },
    {
      headerName: '서비스명',
      field: 'service_name',
      flex: 2,
      minWidth: 200,
      cellStyle: { textAlign: 'left' },
    },
    {
      headerName: '접속자 수',
      field: 'user_count',
      flex: 1,
      minWidth: 120,
      cellStyle: { textAlign: 'center' },
    },
    {
      headerName: '페이지 접속 수',
      field: 'page_count',
      flex: 1,
      minWidth: 150,
      cellStyle: { textAlign: 'center' },
    },
  ],
};

const ServiceListPage = () => {
  const [rowData, setRowData] = useState([]);
  const gridRef = useRef<AgGridReact>(null);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [searchParams, setSearchParams] = useState<any>(null);
  const [selectedPeriod, setSelectedPeriod] = useState<string>(''); // 선택된 기간 버튼 상태
  const [serviceCodes, setServiceCodes] = useState<Array<{ code: string; name: string }>>([]);

  // yyyyMMdd로 변환 (입력이 yyyy-MM-dd거나 Date 문자열이어도 숫자만 취득)
  const toYYYYMMDD = (v?: string) => {
    if (!v) return '';
    const digits = v.replace(/[^0-9]/g, '');
    if (digits.length >= 8) return digits.slice(0, 8);
    const d = new Date(v);
    if (!isNaN(d.getTime())) {
      const yyyy = String(d.getFullYear());
      const mm = String(d.getMonth() + 1).padStart(2, '0');
      const dd = String(d.getDate()).padStart(2, '0');

      return `${yyyy}${mm}${dd}`;
    }

    return '';
  };

  const { register, handleSubmit, setValue, control, getValues } = useForm();
  const { UserInformation } = useRecoilValue(globalState);
  const admin = UserInformation.userId;

  useEffect(() => {
    fetchData();
    // 서비스 코드 로드
    (async () => {
      try {
        const res = await AdminApi.getDomainKindCodes();
        // { cmmn_cd_id, etc_cn_2 }를 사용: 값=cmmn_cd_id, 표시=etc_cn_2
        const list = (res?.data || res || []).map((i: any) => ({ code: i.cmmn_cd_id, name: i.etc_cn_2 }));
        setServiceCodes(list);
      } catch (e) {
        console.error('서비스 코드 로드 실패:', e);
        setServiceCodes([]);
      }
    })();
  }, []);

  // page가 변경될 때마다 데이터를 새로 가져오도록 useEffect 추가
  useEffect(() => {
    fetchData(searchParams);
  }, [page]);

  const fetchData = async (params?: any) => {
    try {
      setLoading(true);

      // 총 개수 조회 (날짜를 yyyyMMdd로 변환해 전달)
      const countParams = {
        searchServiceNm: params?.searchServiceNm,
        searchStartDt: toYYYYMMDD(params?.searchStartDt),
        searchEndDt: toYYYYMMDD(params?.searchEndDt),
      };

      const countResult = await AdminApi.getServiceLogsCount(countParams);
      const totalCount = countResult.data || 0;
      setTotal(totalCount);

      // 서비스 로그 리스트 조회 (날짜를 yyyyMMdd로 변환해 전달)
      const listParams = {
        pageSize: 10,
        pageNo: page,
        searchServiceNm: params?.searchServiceNm,
        searchStartDt: toYYYYMMDD(params?.searchStartDt),
        searchEndDt: toYYYYMMDD(params?.searchEndDt),
      };

      const listResult = await AdminApi.getServiceLogs(listParams);
      const logs = listResult.data || [];

      if (logs.length < 1) {
        setRowData([]);
        return;
      }

      const formattedData = logs.map((log) => ({
        access_date: log.cntn_dt || '',
        service_name: log.service_nm || '',
        user_count: log.user_cnt || 0,
        page_count: log.page_cnt || 0,
        selected: false,
      }));

      setRowData(formattedData);
    } catch (e) {
      console.error('API 호출 실패:', e);
      setTotal(0);
      setRowData([]);
    } finally {
      setLoading(false);
    }
  };

  const deselect = useCallback(() => {
    setRowData((prev) => prev.map((i) => ({ ...i, selected: false })));
  }, []);

  useEffect(() => {
    console.log(rowData);
  }, [rowData]);

  const onFirstDataRendered = (params) => {
    params.api.sizeColumnsToFit();
  };

  const approval = useCallback(async () => {
    const grid = gridRef.current;
    const selectedRow = [];

    grid.api.forEachNode((rowNode) => {
      if (rowNode.data.selected) {
        selectedRow.push(rowNode.data);
      }
    });

    console.log('approval ', selectedRow);

    if (selectedRow.length < 1) {
      alert('선택해주세요.');
      return;
    }

    try {
      // 서비스 통계 관련 작업 처리
      console.log('Selected service stats:', selectedRow);

      alert('처리되었습니다.');
      fetchData();
    } catch (e) {
      console.error('처리 중 오류:', e);
      alert('처리 중 오류가 발생했습니다.');
    }

    deselect();
  }, [gridRef.current, admin]);

  const onSubmit = (formData) => {
    console.log('Search params:', formData);
    setSearchParams(formData); // 검색 파라미터 저장
    setPage(0); // 검색 시 페이지를 0으로 초기화
    fetchData(formData);
  };

  // 기간 버튼 클릭 시 날짜 범위 설정 함수
  const handlePeriodClick = (period: string) => {
    const today = new Date();
    const formatDate = (date: Date) => {
      return date.toISOString().split('T')[0];
    };

    let startDate: Date;
    let endDate = today;

    switch (period) {
      case '1week':
        startDate = new Date(today);
        startDate.setDate(today.getDate() - 7);
        break;
      case '2weeks':
        startDate = new Date(today);
        startDate.setDate(today.getDate() - 14);
        break;
      case '1month':
        startDate = new Date(today);
        startDate.setMonth(today.getMonth() - 1);
        break;
      case '3months':
        startDate = new Date(today);
        startDate.setMonth(today.getMonth() - 3);
        break;
      case 'lastMonth': {
        // 지난달 1일부터 지난달 마지막일까지
        const lastMonth = today.getMonth() === 0 ? 11 : today.getMonth() - 1;
        const lastMonthYear = today.getMonth() === 0 ? today.getFullYear() - 1 : today.getFullYear();

        startDate = new Date(lastMonthYear, lastMonth, 2); // 1일 -> 2일로 변경
        endDate = new Date(lastMonthYear, lastMonth + 1, 1); // 마지막일 -> 마지막일+1로 변경
        break;
      }
      default:
        startDate = today;
    }

    const formattedStartDate = formatDate(startDate);
    const formattedEndDate = formatDate(endDate);

    console.log('Setting dates:', { start: formattedStartDate, end: formattedEndDate });

    // setValue를 개별적으로 호출하고 shouldValidate와 shouldDirty 옵션 추가
    setValue('searchStartDt', formattedStartDate, { shouldValidate: true, shouldDirty: true });
    setValue('searchEndDt', formattedEndDate, { shouldValidate: true, shouldDirty: true });

    setSelectedPeriod(period); // 선택된 기간 상태 업데이트

    // 강제로 DOM 요소에도 값 설정 (fallback)
    const startInput = document.getElementById('searchStartDt') as HTMLInputElement;
    const endInput = document.getElementById('searchEndDt') as HTMLInputElement;

    if (startInput) startInput.value = formattedStartDate;
    if (endInput) endInput.value = formattedEndDate;
  };

  const excelDownload = async () => {
    try {
      const formValues = getValues();
      const params = {
        searchUserNm: searchParams?.searchUserNm ?? formValues?.searchUserNm ?? '',
        searchUserId: searchParams?.searchUserId ?? formValues?.searchUserId ?? '',
        searchServiceNm: searchParams?.searchServiceNm ?? formValues?.searchServiceNm ?? '',
        searchStartDt: toYYYYMMDD(searchParams?.searchStartDt ?? formValues?.searchStartDt ?? ''),
        searchEndDt: toYYYYMMDD(searchParams?.searchEndDt ?? formValues?.searchEndDt ?? ''),
      };
      const res = await AdminApi.downloadServiceLogsExcel(params);
      const blob = new Blob([res.data], { type: res.headers['content-type'] || 'application/vnd.ms-excel' });
      const disposition = res.headers['content-disposition'] || res.headers['Content-Disposition'];
      let filename = 'service_logs.xlsx';
      if (disposition) {
        const match = /filename\*=UTF-8''(?<utf8>[^;]+)|filename="?(?<plain>[^;"]+)"?/i.exec(disposition);
        const picked = match?.groups?.utf8 || match?.groups?.plain || '';
        const rawName = decodeURIComponent(picked.trim());
        if (rawName) filename = rawName;
      }
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      console.error('서비스 엑셀 다운로드 실패:', e);
      alert('엑셀 다운로드 중 오류가 발생했습니다.');
    }
  };

  return (
    <section className="cPage">
      <article className="pageTitArea">
        <div className="pageTitLeft">
          <div
            className="pageTit"
            style={{ marginTop: '16px' }}
          >
            서비스 접속 정보 조회
          </div>
        </div>
      </article>

      <article className="pageConArea">
        <div
          className="pageSearchArea"
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
          }}
        >
          <div className="pageSearchItem pageSearchFirst">
            <label
              className="searchLabel"
              htmlFor="searchServiceNm"
            >
              서비스명
            </label>
            <select
              id="searchServiceNm"
              {...register('searchServiceNm')}
            >
              <option value="">전체</option>
              {serviceCodes.map((opt) => (
                <option
                  key={opt.code}
                  value={opt.code}
                >
                  {opt.name}
                </option>
              ))}
            </select>
          </div>

          {/* 기간 + 검색 버튼을 동일 컨테이너로 묶고 우측 정렬 */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: 1, minWidth: 0 }}>
            <div
              className="pageSearchItem"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                whiteSpace: 'nowrap',
                flexWrap: 'nowrap',
                overflow: 'hidden',
              }}
            >
              <label
                className="searchLabel"
                htmlFor="date"
              >
                기간
              </label>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <div className="date-input-container">
                  <Controller
                    name="searchStartDt"
                    control={control}
                    render={({ field }) => (
                      <ReactDatePicker
                        selected={field.value ? new Date(field.value) : null}
                        onChange={(date: Date | null) => {
                          field.onChange(
                            date
                              ? new Date(date.getTime() - date.getTimezoneOffset() * 60000).toISOString().split('T')[0]
                              : '',
                          );
                        }}
                        dateFormat="yyyy-MM-dd"
                        placeholderText="시작일"
                        className="react-datepicker-input"
                        isClearable
                      />
                    )}
                  />
                </div>
                <span className="date-separator">~</span>
                <div className="date-input-container">
                  <Controller
                    name="searchEndDt"
                    control={control}
                    render={({ field }) => (
                      <ReactDatePicker
                        selected={field.value ? new Date(field.value) : null}
                        onChange={(date: Date | null) => {
                          field.onChange(
                            date
                              ? new Date(date.getTime() - date.getTimezoneOffset() * 60000).toISOString().split('T')[0]
                              : '',
                          );
                        }}
                        dateFormat="yyyy-MM-dd"
                        placeholderText="종료일"
                        className="react-datepicker-input"
                        isClearable
                      />
                    )}
                  />
                </div>
                <div style={{ display: 'flex', gap: '0' }}>
                  <button
                    type="button"
                    onClick={() => handlePeriodClick('1week')}
                    className={`dateRangeBtn ${selectedPeriod === '1week' ? 'active' : ''}`}
                  >
                    1주일
                  </button>
                  <button
                    type="button"
                    onClick={() => handlePeriodClick('2weeks')}
                    className={`dateRangeBtn ${selectedPeriod === '2weeks' ? 'active' : ''}`}
                  >
                    2주일
                  </button>
                  <button
                    type="button"
                    onClick={() => handlePeriodClick('1month')}
                    className={`dateRangeBtn ${selectedPeriod === '1month' ? 'active' : ''}`}
                  >
                    1개월
                  </button>
                  <button
                    type="button"
                    onClick={() => handlePeriodClick('3months')}
                    className={`dateRangeBtn ${selectedPeriod === '3months' ? 'active' : ''}`}
                  >
                    3개월
                  </button>
                  <button
                    type="button"
                    onClick={() => handlePeriodClick('lastMonth')}
                    className={`dateRangeBtn ${selectedPeriod === 'lastMonth' ? 'active' : ''}`}
                  >
                    지난달
                  </button>
                </div>
              </div>
            </div>

            <button
              type="button"
              className="bSearch"
              onClick={handleSubmit(onSubmit)}
              style={{ marginLeft: 'auto' }}
            >
              <span>검색</span>
            </button>
          </div>
        </div>
      </article>

      <article className="pageConArea">
        <div className="cTableWrap mt40">
          <div className="cTableTopArea">
            <div className="cTableTopLeft">
              <ul className="cTableTopData">
                <li>
                  총 <span className="num">{total}</span> 건
                </li>
              </ul>
            </div>
            <div className="cTableTopRight">
              <button
                type="button"
                className="bLineType05 bDownload"
                onClick={excelDownload}
              >
                <span>엑셀 다운로드</span>
              </button>
            </div>
          </div>
          <div className="cListTableArea2">
            <ProgressBackdrop isLoading={loading} />
            <GridArea
              gridRef={gridRef}
              columnDefs={ServiceListGridDefs.columnDefs}
              rowData={rowData}
              total={total}
              page={page}
              isPaging
              paginationPageSize={10}
              setPage={setPage}
              onGridReady={onFirstDataRendered}
              domLayout="autoHeight"
              defaultColDef={{
                editable: false,
                sortable: true,
                filter: true,
                resizable: true,
              }}
            />
          </div>
          <div className="cPagingArea"></div>
        </div>
      </article>
    </section>
  );
};

export default ServiceListPage;
